'use client';

import { Button } from '@ahafood/ui/components/button';
import { cn } from '@ahm/common-helpers';
import { ArrowLeft } from 'lucide-react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useQueryState } from 'nuqs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FilterDrawer } from './filter-drawer';
import type { Payload } from './simple-store-list';
import { getSearchAutocompleteFn } from '@/api/use-search-autocomplete';
import { DebouncedInput } from '@/components/debounced-input';

interface FilterBarProps {
  categories: {
    id: number;
    slug: string;
    title_vi_vn: string;
  }[];
  currentPayload: Payload;
  citySlug: string;
  _payload: Payload;
}

const buildFilteredUrl = (
  citySlug: string,
  filters: {
    category_slug?: string;
    promotion?: boolean;
    freeship?: boolean | string;
    special_request?: string;
    radius?: number;
    open_now?: boolean;
  }
) => {
  let url = `/${citySlug}/danh-sach-cua-hang`;
  if (filters.category_slug) url += `-${filters.category_slug}`;
  const options: string[] = [];
  if (filters.promotion) options.push('co-khuyen-mai');
  if (filters.freeship === 'freeship' || filters.freeship === true)
    options.push('co-freeship');
  if (filters.freeship === 'freeship-100') options.push('co-freeship-100');
  if (filters.radius === 5000) options.push('gan-toi');
  if (filters.open_now) options.push('dang-mo-cua');
  if (filters.special_request === 'new_store') options.push('moi');
  if (options.length > 0) url += '-' + options.join('-');
  return url;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function FilterBar({
  categories,
  currentPayload,
  citySlug,
}: FilterBarProps) {
  const router = useRouter();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    category_slug: currentPayload.category_slug ?? '',
    promotion: currentPayload.promotion ?? false,
    freeship: currentPayload.freeship ?? false,
    special_request: currentPayload.special_request ?? '',
    nearby: currentPayload.radius === 5000,
    open_now: currentPayload.open_now ?? false,
  });

  const [searchQuery] = useQueryState<string>('text', {
    parse: (v) => (typeof v === 'string' ? v : ''),
    defaultValue: '',
  });

  const { control, watch, setValue } = useForm<{ text: string }>({
    defaultValues: { text: searchQuery ?? '' },
  });
  const textValue = watch('text');
  // Debounced text for autocomplete
  const debouncedText = useDebounce(textValue, 300);

  const isSelectingRef = useRef(false);
  const didInitFromUrl = useRef(false);

  // Autocomplete states
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // only autofocus when navigating from StoreHeader
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const shouldAutoFocus = searchParams.get('focus') === '1';
  useEffect(() => {
    if (shouldAutoFocus) {
      inputRef.current?.focus();
      // remove focus param from URL
      const params = new URLSearchParams(searchParams.toString());
      params.delete('focus');
      const newQuery = params.toString();
      router.replace(`${pathname}${newQuery ? `?${newQuery}` : ''}`);
    }
  }, [shouldAutoFocus, searchParams, pathname, router]);

  // navigateWithText (preserve other filters except category)
  const navigateWithText = useCallback(
    (text: string) => {
      const url = buildFilteredUrl(citySlug, {
        category_slug: text ? undefined : currentPayload.category_slug,
        promotion: currentPayload.promotion,
        freeship: currentPayload.freeship,
        special_request: currentPayload.special_request,
        radius: currentPayload.radius,
        open_now: currentPayload.open_now,
      });

      if (text) {
        router.push(`${url}?text=${encodeURIComponent(text)}`);
      } else {
        router.push(url);
      }
    },
    [citySlug, currentPayload, router]
  );

  useEffect(() => {
    if (!didInitFromUrl.current && searchQuery) {
      // lần đầu load từ URL -> chỉ set value, không mở dropdown
      setValue('text', searchQuery);
      didInitFromUrl.current = true;
    }
  }, [searchQuery, setValue]);
  // fetch suggestions when debounced text changes
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (debouncedText && debouncedText.length > 0) {
        if (isSelectingRef.current) {
          isSelectingRef.current = false;
          return;
        }
        setIsLoading(true);
        try {
          const response = await getSearchAutocompleteFn({
            text: debouncedText,
          });
          setSuggestions(response?.suggestions || []);
          if (isFocused) setShowDropdown(true);
        } catch (error) {
          console.error(error);
          setSuggestions([]);
          if (isFocused) setShowDropdown(true);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSuggestions([]);
        setIsLoading(false);
        setShowDropdown(false);
      }
    };
    void fetchSuggestions();
  }, [debouncedText, isFocused]);

  // handle keydown: Enter confirms search and closes dropdown
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        setShowDropdown(false);
        setIsFocused(false);
        navigateWithText(textValue);
        inputRef.current?.blur();
      }
      // optional: allow Escape to close dropdown
      if (e.key === 'Escape') {
        setShowDropdown(false);
        setIsFocused(false);
        inputRef.current?.blur();
      }
    },
    [navigateWithText, textValue]
  );

  // handle blur: don't close dropdown — only mark focus state
  const handleBlur = useCallback(() => {
    // keep a small delay in case suggestion click triggers first
    setTimeout(() => {
      setIsFocused(false);
      // DO NOT setShowDropdown(false) here
    }, 150);
  }, []);

  // handle input focus: mark and if text present open dropdown (suggestions already set by effect)
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    if (textValue && textValue.length > 0) {
      setShowDropdown(true);
    }
  }, [textValue]);

  // user typing
  const handleTextChange = useCallback(
    (val: string) => {
      setValue('text', val);
      // do not navigate immediately — wait for Enter or selection
    },
    [setValue]
  );

  // selecting a suggestion => set text, navigate and close dropdown
  const handleSelectSuggestion = useCallback(
    (suggestion: string) => {
      isSelectingRef.current = true;
      setValue('text', suggestion);
      navigateWithText(suggestion);
      setIsFocused(false);
      setShowDropdown(false);
      inputRef.current?.blur();
    },
    [setValue, navigateWithText]
  );

  const handleClearText = useCallback(() => {
    setValue('text', '');
    navigateWithText('');
    setIsFocused(false);
    setShowDropdown(false);
  }, [setValue, navigateWithText]);

  // filter-related handlers (unchanged)
  const navigateWithFilters = useCallback(
    (filters: typeof tempFilters) => {
      const url = buildFilteredUrl(citySlug, {
        category_slug: filters.category_slug || undefined,
        promotion: filters.promotion,
        freeship: filters.freeship,
        special_request: filters.special_request || undefined,
        radius: filters.nearby ? 5000 : undefined,
        open_now: filters.open_now,
      });
      if (textValue && !filters.category_slug) {
        router.push(`${url}?text=${encodeURIComponent(textValue)}`);
      } else {
        router.push(url);
      }
    },
    [citySlug, router, textValue]
  );

  const handleApplyFilters = useCallback(() => {
    if (tempFilters.category_slug && textValue) {
      setValue('text', '');
      setShowDropdown(false);
    }
    navigateWithFilters(tempFilters);
    setIsFilterOpen(false);
  }, [tempFilters, textValue, setValue, navigateWithFilters]);

  const handleResetFilters = useCallback(() => {
    setTempFilters({
      category_slug: '',
      promotion: false,
      freeship: false,
      special_request: '',
      nearby: false,
      open_now: false,
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setValue('text', '');
    setTempFilters({
      category_slug: '',
      promotion: false,
      freeship: false,
      special_request: '',
      nearby: false,
      open_now: false,
    });
    router.push(`/${citySlug}/danh-sach-cua-hang`);
    setIsFilterOpen(false);
    setShowDropdown(false);
  }, [setValue, router, citySlug]);

  const handleFilterButtonClick = useCallback(
    (filterType: string) => {
      switch (filterType) {
        case 'nearby':
          navigateWithFilters({
            ...tempFilters,
            nearby: currentPayload.radius !== 5000,
          });
          break;
        case 'promotion':
          navigateWithFilters({
            ...tempFilters,
            promotion: !currentPayload.promotion,
          });
          break;
        case 'freeship':
          navigateWithFilters({
            ...tempFilters,
            freeship:
              currentPayload.freeship === 'freeship' ? false : 'freeship',
          });
          break;
        case 'open_now':
          navigateWithFilters({
            ...tempFilters,
            open_now: !currentPayload.open_now,
          });
          break;
        default:
          break;
      }
    },
    [currentPayload, navigateWithFilters, tempFilters]
  );

  const getActiveFilterCount = useCallback(() => {
    let count = 0;
    if (currentPayload.category_slug && !textValue) count++;
    if (currentPayload.promotion) count++;
    if (currentPayload.freeship) count++;
    if (currentPayload.special_request) count++;
    if (currentPayload.radius === 5000) count++;
    if (currentPayload.open_now) count++;
    return count;
  }, [currentPayload, textValue]);

  return (
    <>
      <div className="bg-white fixed top-0 inset-x-0 z-50 px-4 pt-4 pb-2 w-full max-w-screen-xl mx-auto">
        <div className="flex gap-2 mb-4 items-center">
          <Button
            variant="ghost"
            className="hover:bg-neutral-10 py-2 px-1 rounded-md transition-colors [&_svg]:size-5"
            onClick={() => router.push(`/${citySlug}`)}
          >
            <ArrowLeft />
          </Button>

          {currentPayload.special_request !== 'new_store' ? (
            <div className="relative flex-1 flex items-center">
              <Controller
                name="text"
                control={control}
                render={({ field }) => (
                  <DebouncedInput
                    ref={inputRef}
                    value={field.value}
                    onChange={handleTextChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    debounce={400}
                    placeholder="Tìm cửa hàng..."
                    className="w-full pl-12 pr-4 py-3 bg-neutral-10 rounded-2xl h-12 text-neutral-90 focus:outline-none focus:ring-2 focus:ring-primary-5 border-none"
                  />
                )}
              />

              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg
                  className="size-5 text-neutral-50"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              {textValue && (
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-40 hover:text-neutral-60 transition-colors"
                  onClick={handleClearText}
                >
                  <svg
                    className="size-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          ) : (
            <div className="w-full text-center pr-8">
              <span className="font-medium text-lg">{`Cửa hàng mới`}</span>
            </div>
          )}
        </div>

        {showDropdown ? (
          /* Autocomplete Suggestions */
          <div className="pb-4 h-dvh">
            <div className="bg-white rounded-lg">
              {isLoading ? (
                <div className="space-y-1">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div
                      key={index}
                      className="p-3 border-b border-neutral-10 last:border-b-0"
                    >
                      <div className="flex items-center animate-pulse">
                        <div className="size-5 bg-neutral-20 rounded mr-3 shrink-0">
                          <svg
                            className="size-5 text-neutral-30"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        <div
                          className="h-4 bg-neutral-20 rounded flex-1"
                          style={{ width: `${60 + Math.random() * 30}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : suggestions.length > 0 ? (
                <div className="space-y-0">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      className="w-full px-2 py-4 text-sm text-left hover:bg-neutral-10 cursor-pointer border-b border-neutral-10 last:border-b-0 transition-colors focus:outline-none focus:bg-neutral-10 flex items-center"
                      onClick={() => handleSelectSuggestion(suggestion)}
                      // prevent default on mouseDown to ensure suggestion click registers before blur
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <svg
                        className="size-5 text-neutral-50 mr-3 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      <span className="truncate text-neutral-90">
                        {suggestion}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-neutral-50">
                  <svg
                    className="size-12 mx-auto mb-3 text-neutral-30"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <p>Không tìm thấy kết quả phù hợp</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Normal Filter Buttons */
          <div className="flex gap-2 scrollbar-hide overflow-x-scroll items-center">
            <FilterDrawer
              isOpen={isFilterOpen}
              setIsOpen={setIsFilterOpen}
              tempFilters={tempFilters}
              setTempFilters={setTempFilters}
              categories={categories}
              textValue={textValue}
              activeCount={getActiveFilterCount()}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            />

            {/* quick filters... kept same */}
            <Button
              variant="outline"
              onClick={() => handleFilterButtonClick('nearby')}
              className={cn(
                'gap-2 whitespace-nowrap rounded-xl border flex items-center px-3 py-1 text-neutral-70 hover:bg-white transition-colors',
                currentPayload.radius === 5000 && 'border-primary-50'
              )}
            >
              Gần tôi
            </Button>

            <Button
              variant="outline"
              onClick={() => handleFilterButtonClick('promotion')}
              className={cn(
                'gap-2 whitespace-nowrap rounded-xl border flex items-center px-3 py-1 text-neutral-70  hover:bg-white transition-colors',
                currentPayload.promotion && 'border-primary-50'
              )}
            >
              Ưu đãi
            </Button>

            <Button
              variant="outline"
              onClick={() => handleFilterButtonClick('freeship')}
              className={cn(
                'gap-2 whitespace-nowrap border rounded-xl flex items-center px-3 py-1 text-neutral-70  hover:bg-white transition-colors',
                currentPayload.freeship === 'freeship' && 'border-primary-50'
              )}
            >
              Freeship
            </Button>

            <Button
              variant="outline"
              onClick={() => handleFilterButtonClick('open_now')}
              className={cn(
                'gap-2 whitespace-nowrap border rounded-xl flex items-center px-3 py-1 text-neutral-70  hover:bg-white transition-colors',
                currentPayload.open_now && 'border-primary-50'
              )}
            >
              Mở cửa
            </Button>

            {getActiveFilterCount() > 0 && (
              <Button
                variant="secondary"
                onClick={handleClearAllFilters}
                className="gap-2 whitespace-nowrap bg-red-10 rounded-xl text-red-50 hover:bg-red-20 flex items-center px-3 py-1"
              >
                Đặt lại
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// Add a generic debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}
